import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '../components/common/Container';
import { Heading } from '../components/common/TextComponents';
import Button from '../components/common/Button';
import { Logo } from '../components/common/Logo';
import { useAuthContext } from '../hooks/useAuthContext';
import { 
  saveReflectionVideo, 
  getUserReflections, 
  deleteReflectionVideo 
} from '../services/firebase';

const QUESTIONS = [
  "Why are you burnt out today?",
  "What would you want to be doing if money were no object?",
  "What's the most fulfilling project you've worked on recently?",
  "What's something you wish you could change about your current role?",
  "What are you most proud of?"
];

const CATEGORIES = [
  "Self-Reflection",
  "Aspirations",
  "Fulfillment",
  "Improvement",
  "Achievements"
];

const ReflectionQuestionsPage = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingTime, setRecordingTime] = useState(60); // 1 minute countdown timer
  const [error, setError] = useState('');
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showReviewMode, setShowReviewMode] = useState(false);
  
  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Load user's existing reflections
  useEffect(() => {
    const loadUserReflections = async () => {
      if (user) {
        try {
          setIsLoading(true);
          const result = await getUserReflections(user.uid);
          
          if (result.success) {
            // Set completed questions based on existing reflections
            setCompletedQuestions(result.data || []);
            
            // If there are completed questions, start at the next unanswered question
            if (result.data && result.data.length > 0 && result.data.length < QUESTIONS.length) {
              setCurrentQuestionIndex(result.data.length);
            }
          }
        } catch (err) {
          console.error('Error loading reflections:', err);
          setError('Could not load your existing reflections.');
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadUserReflections();
    
    // Cleanup function
    return () => {
      // Stop camera if it's running
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Clear timers
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user]);
  
  const startCamera = async () => {
    try {
      // Stop any existing streams first
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach(track => track.stop());
      }

      // Request media with specific audio constraints for better quality
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user"
        }, 
        audio: { 
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 2
        }
      });
      
      // Save the stream reference
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError("Camera access denied. Please allow camera access to record your video.");
      return null;
    }
  };
  
  const startRecording = async () => {
    setError('');
    setRecordingTime(60); // Reset timer to 60 seconds
    
    // Reset any previous recording
    setVideoBlob(null);
    setShowPreview(false);
    
    // Start camera
    const stream = await startCamera();
    if (!stream) return;
    
    // Initialize media recorder with the stream
    try {
      // Try different codecs in order of preference
      const mimeTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=h264,opus',
        'video/webm',
        'video/mp4'
      ];
      
      let options = { 
        mimeType: mimeTypes[0],
        audioBitsPerSecond: 128000, // Better audio quality
        videoBitsPerSecond: 2500000 // Reasonable video quality
      };
      
      let mediaRecorder = null;
      
      // Try each mime type until we find one that works
      for (let i = 0; i < mimeTypes.length; i++) {
        try {
          options.mimeType = mimeTypes[i];
          mediaRecorder = new MediaRecorder(stream, options);
          console.log("Using mime type:", mimeTypes[i]);
          break;
        } catch (e) {
          console.log(`${mimeTypes[i]} is not supported, trying next format`);
          if (i === mimeTypes.length - 1) {
            throw new Error("No supported video format found");
          }
        }
      }
      
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        // Create a blob with the collected chunks
        const blob = new Blob(chunks, { type: mediaRecorder.mimeType });
        setVideoBlob(blob);
        setShowPreview(true);
      };
      
      // Start recording with a higher timeslice value (2000ms) to reduce chunking
      mediaRecorder.start(2000);
      setRecording(true);
      
      // Start timer to track recording duration (countdown from 60 seconds)
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            stopRecording();
            return 0;
          }
          return newTime;
        });
      }, 1000);
      
      // Automatically stop recording after 1 minute (60000 ms)
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopRecording();
        }
      }, 60000);
    } catch (error) {
      console.error("Error initializing media recorder:", error);
      setError("Your browser doesn't support the necessary video recording features. Please try a different browser or device.");
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
      
      // Clear the recording timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };
  
  const retakeVideo = () => {
    setShowPreview(false);
    setVideoBlob(null);
    startRecording();
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  const handleSubmit = async () => {
    if (!videoBlob) {
      setError('Please record a video before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    setUploadProgress(0);
    
    try {
      // Upload video to Firebase
      const videoResult = await saveReflectionVideo(
        user.uid, 
        videoBlob, 
        currentQuestionIndex,
        QUESTIONS[currentQuestionIndex]
      );
      
      if (videoResult.success) {
        // Update completed questions
        setCompletedQuestions(prev => {
          const updated = [...prev];
          updated[currentQuestionIndex] = {
            questionIndex: currentQuestionIndex,
            questionText: QUESTIONS[currentQuestionIndex],
            videoUrl: videoResult.videoUrl,
            storagePath: videoResult.storagePath,
            timestamp: videoResult.timestamp
          };
          return updated;
        });
        
        // Move to next question if there are more
        if (currentQuestionIndex < QUESTIONS.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
        
        // Reset state for next question
        setVideoBlob(null);
        setShowPreview(false);
        setUploadProgress(100);
        
        // Short delay before resetting
        setTimeout(() => {
          setIsSubmitting(false);
          setUploadProgress(0);
        }, 1000);
      } else {
        throw new Error(videoResult.error || "Failed to upload video");
      }
    } catch (error) {
      console.error("Error submitting video:", error);
      setError(error.message || "An unexpected error occurred. Please try again later.");
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteReflection = async (index) => {
    if (!user) return;
    
    try {
      const reflection = completedQuestions[index];
      if (!reflection) return;
      
      const result = await deleteReflectionVideo(user.uid, index, reflection.storagePath);
      
      if (result.success) {
        // Update completed questions
        setCompletedQuestions(prev => {
          const updated = [...prev];
          updated.splice(index, 1);
          return updated;
        });
        
        // Update current question index if needed
        if (currentQuestionIndex > index) {
          setCurrentQuestionIndex(prev => prev - 1);
        } else if (currentQuestionIndex === QUESTIONS.length - 1 && completedQuestions.length === QUESTIONS.length) {
          setCurrentQuestionIndex(completedQuestions.length - 1);
        }
      } else {
        setError("Failed to delete reflection. Please try again.");
      }
    } catch (err) {
      console.error("Error deleting reflection:", err);
      setError("An error occurred while deleting your reflection.");
    }
  };
  
  const handleRedoAll = async () => {
    if (window.confirm("Are you sure you want to delete all your reflections and start over?")) {
      try {
        // Delete all reflections one by one
        for (let i = 0; i < completedQuestions.length; i++) {
          const reflection = completedQuestions[i];
          if (reflection) {
            await deleteReflectionVideo(user.uid, i, reflection.storagePath);
          }
        }
        
        // Reset state
        setCompletedQuestions([]);
        setCurrentQuestionIndex(0);
        setShowReviewMode(false);
      } catch (err) {
        console.error("Error resetting reflections:", err);
        setError("Failed to reset reflections. Please try again.");
      }
    }
  };
  
  const isAllQuestionsCompleted = completedQuestions.filter(Boolean).length === QUESTIONS.length;
  
  // If loading, show loading state
  if (isLoading) {
    return (
      <div className="py-24 relative min-h-screen sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100 to-white opacity-10 -z-10"></div>
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <Logo className="h-12 mx-auto mb-8" />
            <Heading>Loading your reflections</Heading>
            <div className="mt-8 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent"></div>
            </div>
          </div>
        </Container>
      </div>
    );
  }
  
  // Review mode for completed questions
  if (showReviewMode || (isAllQuestionsCompleted && !showPreview && !recording)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="py-6 px-6">
          <div className="flex justify-between items-center mb-6">
            <Logo className="h-8" />
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Heading as="h1">Your Career Reflections</Heading>
              <p className="mt-4 text-xl text-gray-600">
                Review your recorded insights that will help guide your career transition.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-8 mb-8">
              <div className="grid grid-cols-1 gap-6">
                {completedQuestions.map((reflection, index) => (
                  reflection && (
                    <div key={index} className="border border-gray-100 rounded-lg overflow-hidden shadow-sm">
                      <div className="bg-amber-50 p-4 flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-amber-500 uppercase tracking-wider">{CATEGORIES[index]}</div>
                          <h3 className="text-lg font-medium text-gray-900">{QUESTIONS[index]}</h3>
                        </div>
                        <button 
                          onClick={() => handleDeleteReflection(index)}
                          className="text-gray-400 hover:text-red-500 p-1"
                          title="Delete this reflection"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <video 
                        className="w-full aspect-video" 
                        src={reflection.videoUrl} 
                        controls
                      />
                    </div>
                  )
                ))}
              </div>
              
              <div className="mt-8 flex justify-center space-x-4">
                <Button 
                  variant="outline" 
                  onClick={handleRedoAll}
                  className="text-red-600 hover:bg-red-50"
                >
                  Start Over
                </Button>
                <Button onClick={() => navigate('/dashboard')}>
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Main recording interface
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-6 px-6">
        {/* Brand logo */}
        <div className="flex justify-center mb-8">
          <Logo className="h-8" />
        </div>
        
        {/* 1. Progress steps at the top */}
        <div className="max-w-3xl mx-auto mb-12">
          <nav aria-label="Progress">
            <ol role="list" className="flex items-center justify-center">
              {QUESTIONS.map((_, stepIdx) => {
                // Determine step status
                let status = 'upcoming';
                if (stepIdx < currentQuestionIndex || (completedQuestions[stepIdx] && completedQuestions[stepIdx] !== null)) {
                  status = 'complete';
                } else if (stepIdx === currentQuestionIndex) {
                  status = 'current';
                }
                
                return (
                  <li key={stepIdx} className={`${stepIdx !== QUESTIONS.length - 1 ? 'pr-8 sm:pr-16' : ''} relative`}>
                    {status === 'complete' ? (
                      <>
                        <div aria-hidden="true" className="absolute inset-0 flex items-center">
                          <div className="h-0.5 w-full bg-amber-600" />
                        </div>
                        <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-amber-600">
                          <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </>
                    ) : status === 'current' ? (
                      <>
                        <div aria-hidden="true" className="absolute inset-0 flex items-center">
                          <div className="h-0.5 w-full bg-gray-200" />
                        </div>
                        <div
                          className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-amber-600 bg-white"
                          aria-current="step"
                        >
                          <span className="h-2.5 w-2.5 rounded-full bg-amber-600" aria-hidden="true" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div aria-hidden="true" className="absolute inset-0 flex items-center">
                          <div className="h-0.5 w-full bg-gray-200" />
                        </div>
                        <div className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                          <span className="h-2.5 w-2.5 rounded-full bg-transparent" aria-hidden="true" />
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row">
            {/* Left side - Main content */}
            <div className="w-full md:w-2/3 mx-auto flex flex-col justify-center">
              {/* 2. Question display (focus on just the question) */}
              <div className="mb-12 text-center">
                <div className="text-sm font-semibold text-amber-600 uppercase tracking-wider mb-2">
                  {CATEGORIES[currentQuestionIndex]}
                </div>
                <h2 className="text-3xl font-bold text-gray-900 leading-tight">
                  {QUESTIONS[currentQuestionIndex]}
                </h2>
              </div>
              
              {/* 3. Countdown timer (below the question) */}
              {recording && (
                <div className="flex justify-center mb-8">
                  <div className={`px-4 py-2 rounded-full ${
                    recordingTime < 10 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  } font-mono text-lg font-semibold`}>
                    {formatTime(recordingTime)}
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex justify-center space-x-4">
                {!recording && !showPreview && (
                  <Button onClick={startRecording}>
                    Start Recording
                  </Button>
                )}
                
                {recording && (
                  <Button 
                    variant="secondary" 
                    onClick={stopRecording}
                  >
                    Stop Recording
                  </Button>
                )}
                
                {showPreview && (
                  <>
                    <Button 
                      variant="secondary" 
                      onClick={retakeVideo}
                    >
                      Record Again
                    </Button>
                    <Button 
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Reflection'}
                    </Button>
                  </>
                )}
              </div>
              
              {/* Error display */}
              {error && (
                <div className="mt-6 p-4 bg-red-50 rounded-lg text-red-700 text-center">
                  {error}
                </div>
              )}
              
              {/* Upload progress */}
              {isSubmitting && uploadProgress > 0 && (
                <div className="mt-6">
                  <div className="w-full bg-gray-200 h-2 rounded-full">
                    <div 
                      className="bg-amber-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-2">
                    Uploading: {Math.round(uploadProgress)}%
                  </p>
                </div>
              )}
            </div>
            
            {/* Right side - Video preview */}
            <div className="w-full md:w-1/3 p-4">
              {/* 4. Video preview (smaller and to the side) */}
              <div className="bg-black rounded-lg overflow-hidden shadow-md">
                {showPreview ? (
                  <video 
                    ref={videoRef}
                    src={videoBlob ? URL.createObjectURL(videoBlob) : ''} 
                    controls
                    className="w-full aspect-video object-cover"
                  />
                ) : (
                  <div className="relative">
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      muted
                      className="w-full aspect-video object-cover"
                      playsInline
                    />
                    
                    {recording && (
                      <div className="absolute top-2 left-2 flex items-center bg-black bg-opacity-50 px-2 py-1 rounded-full">
                        <div className="h-3 w-3 rounded-full bg-red-500 mr-2 animate-pulse"></div>
                        <span className="text-white text-xs">Recording</span>
                      </div>
                    )}
                    
                    {!videoRef.current?.srcObject && !recording && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                        <p className="text-gray-400 text-sm text-center p-4">
                          Camera preview will appear here when you start recording
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Optional: Review completed reflections link */}
              {completedQuestions.filter(Boolean).length > 0 && (
                <div className="mt-4 text-center">
                  <button 
                    onClick={() => setShowReviewMode(true)}
                    className="text-amber-600 hover:text-amber-800 text-sm font-medium"
                  >
                    Review my completed reflections
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReflectionQuestionsPage;